import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { Button, Input, Form as AntForm, Alert } from 'antd';
import { Container, Row, Col, Form } from 'react-bootstrap';

const CallComponent = () => {
  const [stringeeClient, setStringeeClient] = useState(null);
  const [callObject, setCallObject] = useState(null);
  const [callStatus, setCallStatus] = useState('');
  const [toNumber, setToNumber] = useState('');

  // State lưu thời gian cuộc gọi dựa vào currentTime của audio stream
  const [callSeconds, setCallSeconds] = useState(0);
  const [callStartTime, setCallStartTime] = useState(null);
  const [lastCallSeconds, setLastCallSeconds] = useState(null);

  // Ref cho audio stream
  const remoteAudioRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL;
  const fromNumber = process.env.REACT_APP_STRINGEE_NUMBER || '842471080699';

  // Hàm lưu log cuộc gọi thông qua axios (dùng khi có thể bất đồng bộ)
  const saveCallLog = useCallback((callEvent) => {
    // Lấy username và callerName từ localStorage
    const username = localStorage.getItem('username') || 'default_user';
    const callerName = localStorage.getItem('name') || '';
    // Thêm trường toNumber vào payload để lưu số điện thoại được gọi
    const logData = {
      username,
      callerName,
      toNumber, // lưu số điện thoại được gọi
      callStartTime,  // ISO string
      callDuration: callSeconds, // Tổng số giây
      recordingDetails: null, // Nếu có ghi âm, cập nhật tại đây
      callEvent: callEvent
    };

    console.log('Payload gửi log:', logData);

    // Kiểm tra các trường bắt buộc: username, callerName, callStartTime, callDuration, toNumber
    if (!username || !callerName || !callStartTime || callSeconds === null || !toNumber) {
      console.error('Thiếu thông tin bắt buộc, không gửi log.');
      return;
    }

    axios.post(`${API_URL}/call/log`, logData)
      .then((res) => console.log('Call log saved:', res.data))
      .catch((err) => console.error('Error saving call log:', err));
  }, [API_URL, callSeconds, callStartTime, toNumber]);

  // Hàm lưu log đồng bộ khi khách hàng tắt máy (sử dụng navigator.sendBeacon)
  const sendCallLogOnUnload = useCallback(() => {
    if (!callStartTime) return;
    const finalSeconds = remoteAudioRef.current
      ? Math.floor(remoteAudioRef.current.currentTime)
      : callSeconds;
    const username = localStorage.getItem('username') || 'default_user';
    const callerName = localStorage.getItem('name') || '';
    const logData = {
      username,
      callerName,
      toNumber,
      callStartTime,
      callDuration: finalSeconds,
      recordingDetails: null,
      callEvent: { reason: 'Page unload' }
    };
    console.log('Payload gửi log (unload):', logData);
    const payload = JSON.stringify(logData);
    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      navigator.sendBeacon(`${API_URL}/call/log`, blob);
    } else {
      // fallback synchronous XHR nếu sendBeacon không khả dụng
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${API_URL}/call/log`, false);
      xhr.setRequestHeader("Content-Type", "application/json");
      xhr.send(payload);
    }
  }, [API_URL, callSeconds, callStartTime, toNumber]);

  // Gắn sự kiện beforeunload để lưu log khi khách hàng tắt máy
  useEffect(() => {
    window.addEventListener('beforeunload', sendCallLogOnUnload);
    return () => {
      window.removeEventListener('beforeunload', sendCallLogOnUnload);
    };
  }, [sendCallLogOnUnload]);

  useEffect(() => {
    if (!window.StringeeClient || !window.StringeeCall) {
      console.error('SDK Stringee chưa được tải. Kiểm tra lại file index.html.');
      return;
    }

    const STRINGEE_SERVER_ADDRS = [
      "wss://v1.stringee.com:6899/", 
      "wss://v2.stringee.com:6899/"
    ];
    const client = new window.StringeeClient(STRINGEE_SERVER_ADDRS);
    setStringeeClient(client);

    client.on('connect', () => {
      console.log('Đã kết nối tới Stringee');
      setCallStatus('Connected');
    });
    client.on('authen', (res) => {
      if (res.r === 0) {
        console.log('Xác thực thành công, userId:', res.userId);
      } else {
        console.error('Xác thực thất bại:', res.message);
      }
    });
    client.on('disconnect', () => {
      console.log('Đã ngắt kết nối khỏi Stringee');
      setCallStatus('Disconnected');
    });
    client.on('requestnewtoken', () => {
      console.log('Yêu cầu token mới');
      axios.get(`${API_URL}/stringee/token`)
        .then((response) => {
          client.connect(response.data.token);
        })
        .catch((err) => console.error('Lỗi lấy token mới:', err));
    });
    client.on('incomingcall', (incomingCall) => {
      console.log('Có cuộc gọi đến:', incomingCall);
      // Xử lý cuộc gọi đến nếu cần
    });

    // Lấy token từ backend
    axios.get(`${API_URL}/stringee/token`)
      .then((response) => {
        client.connect(response.data.token);
      })
      .catch((error) => {
        console.error('Lỗi khi lấy token:', error);
        setCallStatus('Token Error');
      });

    return () => {
      client.disconnect();
    };
  }, [API_URL]);

  // Các hàm xử lý bàn phím số (Dial Pad)
  const handleDigitClick = (digit) => {
    setToNumber(prev => prev + digit);
  };
  const handleClear = () => {
    setToNumber('');
  };
  const handleBackspace = () => {
    setToNumber(prev => prev.slice(0, -1));
  };

  // Hàm thực hiện cuộc gọi
  const handleCall = () => {
    if (!stringeeClient) {
      alert('Chưa kết nối tới Stringee!');
      return;
    }
    if (!toNumber.trim()) {
      alert('Vui lòng nhập số điện thoại cần gọi.');
      return;
    }

    // Chuyển đổi số điện thoại: nếu nhập "0906793604" thì chuyển thành "84906793604"
    let dialNumber = toNumber.trim();
    if (dialNumber === "0906793604") {
      dialNumber = "84906793604";
    }

    // Reset các state liên quan đến cuộc gọi
    setCallSeconds(0);
    // Đặt callStartTime ngay khi bắt đầu cuộc gọi
    const startTime = new Date().toISOString();
    setCallStartTime(startTime);
    setLastCallSeconds(null);

    const call = new window.StringeeCall(stringeeClient, fromNumber, dialNumber, false);
    setCallObject(call);

    call.on('addstream', (stream) => {
      console.log('addstream: Đã nhận stream của cuộc gọi:', stream);
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = stream;
        remoteAudioRef.current.play().catch((err) =>
          console.error('Lỗi khi phát audio từ addstream:', err)
        );
      }
    });

    call.on('addremotestream', (stream) => {
      console.log('addremotestream: Đã nhận remote stream:', stream);
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = stream;
        remoteAudioRef.current.play().catch((err) =>
          console.error('Lỗi khi phát audio từ addremotestream:', err)
        );
      }
    });

    call.on('signalingstate', (state) => {
      console.log('Trạng thái signaling:', state);
      setCallStatus(state.reason);
      // Nếu cuộc gọi kết thúc (state.code === 6), kết thúc bởi remote hoặc do lỗi mạng
      if (
        state.code === 6 ||
        (state.reason && state.reason.toLowerCase().includes("remote"))
      ) {
        const finalSeconds = remoteAudioRef.current
          ? Math.floor(remoteAudioRef.current.currentTime)
          : callSeconds;
        setCallSeconds(finalSeconds);
        setLastCallSeconds(finalSeconds);
        setCallObject(null);
        saveCallLog(state);
        setCallStartTime(null);
      }
    });

    // Xử lý lỗi để log cuộc gọi ngay cả khi lỗi mạng giữa chừng
    call.on('error', (err) => {
      console.error('Lỗi cuộc gọi:', err);
      setCallStatus('Call error: ' + err.message);
      if (callStartTime) {
        const finalSeconds = remoteAudioRef.current
          ? Math.floor(remoteAudioRef.current.currentTime)
          : callSeconds;
        setCallSeconds(finalSeconds);
        setLastCallSeconds(finalSeconds);
        saveCallLog({ reason: 'Call error: ' + err.message });
        setCallStartTime(null);
      }
    });

    call.makeCall((res) => {
      if (res.r === 0) {
        console.log('Cuộc gọi đang được thực hiện...');
        setCallStatus('Calling...');
      } else {
        console.error('Cuộc gọi không thành công:', res.message);
        setCallStatus('Call failed: ' + res.message);
        // Nếu không thành công, log ngay cả khi không có cuộc gọi được thiết lập
        if (callStartTime) {
          saveCallLog({ reason: 'Call failed: ' + res.message });
          setCallStartTime(null);
        }
      }
    });
  };

  // Hàm ngắt cuộc gọi khi người dùng nhấn "Hủy"
  const handleHangup = () => {
    let finalSeconds = callSeconds;
    if (remoteAudioRef.current) {
      finalSeconds = Math.floor(remoteAudioRef.current.currentTime);
    }
    if (callObject) {
      callObject.hangup((res) => {
        console.log('Cuộc gọi đã kết thúc:', res);
        setCallStatus('Call ended');
      });
      setCallObject(null);
    }
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
    }
    // Lưu log ngay cả khi cuộc gọi bị hủy sớm
    if (callStartTime) {
      setCallSeconds(finalSeconds);
      setLastCallSeconds(finalSeconds);
      saveCallLog({ reason: 'Manual hangup' });
      setCallStartTime(null);
    }
  };

  // Handler của sự kiện timeupdate của thẻ audio, cập nhật callSeconds dựa vào currentTime
  const handleTimeUpdate = (e) => {
    const currentSeconds = Math.floor(e.target.currentTime);
    setCallSeconds(currentSeconds);
  };

  return (
    <Container className="mt-4">
      <Row>
        <Col md={{ span: 6, offset: 3 }}>
          <h2 className="mb-4">Liên hệ khách hàng</h2>
          <AntForm>
            <Form.Group controlId="toNumber" className="mb-3">
              <Form.Label>Số điện thoại</Form.Label>
              <Input
                placeholder="Nhập số điện thoại"
                value={toNumber}
                onChange={(e) => setToNumber(e.target.value)}
              />
            </Form.Group>

            {/* Dial Pad */}
            <div style={{ marginBottom: '1rem' }}>
              <Row>
                <Col xs={4}><Button block onClick={() => handleDigitClick('1')}>1</Button></Col>
                <Col xs={4}><Button block onClick={() => handleDigitClick('2')}>2</Button></Col>
                <Col xs={4}><Button block onClick={() => handleDigitClick('3')}>3</Button></Col>
              </Row>
              <Row className="mt-2">
                <Col xs={4}><Button block onClick={() => handleDigitClick('4')}>4</Button></Col>
                <Col xs={4}><Button block onClick={() => handleDigitClick('5')}>5</Button></Col>
                <Col xs={4}><Button block onClick={() => handleDigitClick('6')}>6</Button></Col>
              </Row>
              <Row className="mt-2">
                <Col xs={4}><Button block onClick={() => handleDigitClick('7')}>7</Button></Col>
                <Col xs={4}><Button block onClick={() => handleDigitClick('8')}>8</Button></Col>
                <Col xs={4}><Button block onClick={() => handleDigitClick('9')}>9</Button></Col>
              </Row>
              <Row className="mt-2">
                <Col xs={4}><Button block onClick={handleClear}>Clear</Button></Col>
                <Col xs={4}><Button block onClick={() => handleDigitClick('0')}>0</Button></Col>
                <Col xs={4}><Button block onClick={handleBackspace}>Back</Button></Col>
              </Row>
            </div>

            <Form.Group>
              <Button type="primary" onClick={handleCall} disabled={!stringeeClient || callObject}>
                Gọi
              </Button>{' '}
              <Button type="default" onClick={handleHangup} disabled={!callObject}>
                Hủy
              </Button>
            </Form.Group>
          </AntForm>

          {/* Hiển thị trạng thái cuộc gọi */}
          {callStatus && (
            <Alert message={`Trạng thái: ${callStatus}`} type="info" showIcon className="mt-3" />
          )}

          {/* Hiển thị thời gian cuộc gọi đang diễn ra */}
          {callObject && (
            <div style={{ marginTop: '1rem' }}>
              <strong>Thời gian cuộc gọi: {callSeconds} giây</strong>
            </div>
          )}

          {/* Hiển thị thời gian cuộc gọi cuối sau khi kết thúc */}
          {!callObject && lastCallSeconds !== null && (
            <div style={{ marginTop: '1rem' }}>
              <strong>Thời gian cuộc gọi cuối: {lastCallSeconds} giây</strong>
            </div>
          )}

          {/* Audio element để phát stream và theo dõi currentTime */}
          <audio 
            ref={remoteAudioRef} 
            onTimeUpdate={handleTimeUpdate}
            style={{ display: 'none' }} 
          />
        </Col>
      </Row>
    </Container>
  );
};

export default CallComponent;
